import { NafThemeType, borderRadius, breakpoints, spacing, themeLight } from '@naf/theme';
import { PortableText } from '@portabletext/react';
import { Text } from '@naf/text';
import styled from 'styled-components';
import { ColorVariant } from '../../../../../../types/featuredBenefitBlockType';

export const StyledButtonGroup = styled.div<{ $breakEarly?: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: fit-content;

  button:not(:last-child),
  a:not(:last-child) {
    margin-bottom: ${spacing.space24};
  }

  @media (max-width: ${({ $breakEarly }) => ($breakEarly ? breakpoints.l : breakpoints.m)}) {
    width: 100%;
    button,
    a {
      width: 100%;
    }
  }
`;

export const Steps = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-bottom: ${spacing.space64};

  div:not(:last-child) {
    margin-right: ${spacing.space40};
  }

  @media (max-width: ${breakpoints.m}) {
    flex-direction: column;
    margin-bottom: ${spacing.space40};
    div:not(:last-child) {
      margin-right: 0;
    }
  }
`;

export const StyledStep = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  width: 0;

  @media (max-width: ${breakpoints.m}) {
    flex: 1;
    width: 100%;
    margin-bottom: ${spacing.space24};
  }
`;

export const StepCount = styled.div<{ $variant: ColorVariant }>`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.ROUND};
  margin-bottom: ${spacing.space8};
  background-color: ${({ theme }) =>
    theme?.typography ? theme.typography.defaultText : themeLight.typography.defaultText};
  color: ${({ $variant, theme }) => {
    const activeTheme = theme?.background ? theme : themeLight;
    return $variant === 'signature' ? activeTheme.background.important : activeTheme.background.brand;
  }};

  * {
    margin: 0;
  }
`;

export const Wrapper = styled.div<{ $variant: ColorVariant; $breakEarly?: boolean }>`
  margin: 0 ${spacing.space40};
  display: flex;
  justify-content: center;
  padding: ${({ $variant }) =>
    $variant === 'outline' ? `0 ${spacing.space40}` : `${spacing.space80} ${spacing.space40} 0`};
  border-radius: ${borderRadius.M};
  overflow: visible;
  position: relative;

  background-color: ${({ $variant, theme }) => {
    const activeTheme: NafThemeType = theme.background ? theme : themeLight;
    if ($variant === 'signature') {
      return activeTheme.background.important;
    }
    if ($variant === 'spruce') {
      return activeTheme.background.brandInverted;
    }
    if ($variant === 'outline') {
      return activeTheme.background.default;
    }
    return activeTheme.background.brand;
  }};

  color: ${({ $variant, theme }) => {
    const activeTheme: NafThemeType = theme.typography ? theme : themeLight;
    return $variant === 'spruce' ? activeTheme.typography.brandTextInverted : activeTheme.typography.defaultText;
  }};

  @media (max-width: ${({ $breakEarly }) => ($breakEarly ? breakpoints.l : breakpoints.m)}) {
    padding: ${spacing.space40} ${spacing.space24};
    margin: 0;
  }
`;

export const Content = styled.div`
  max-width: 1120px;
  width: 100%;
`;

export const Top = styled.div`
  width: 60%;
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-bottom: ${spacing.space64};

  @media (max-width: ${breakpoints.m}) {
    width: unset;
    margin-bottom: ${spacing.space40};
  }
`;

export const Tag = styled(Text)<{ $styleVariant: ColorVariant }>`
  font-weight: bold;
  text-transform: uppercase;
  font-size: 0.875rem;
  line-height: 0.875rem;
  width: fit-content;
  padding: ${spacing.space4} ${spacing.space8};
  border-radius: ${borderRadius.M};
  margin: 0 0 ${spacing.space8};

  background-color: ${({ $styleVariant, theme }) => {
    const activeTheme = theme.componentColors ? theme : themeLight;
    if ($styleVariant === ColorVariant.signature || $styleVariant === ColorVariant.outline) {
      return activeTheme.componentColors.cta.primary;
    }
    if ($styleVariant === ColorVariant.spruce) {
      return activeTheme.background.default;
    }
    return activeTheme.componentColors.cta.secondaryInverted;
  }};

  color: ${({ $styleVariant, theme }) => {
    const activeTheme: NafThemeType = theme?.typography ? theme : themeLight;
    if ($styleVariant === ColorVariant.signature) {
      return activeTheme.typography.brandText;
    }
    if ($styleVariant === ColorVariant.spruce) {
      return activeTheme.typography.brandText;
    }
    return activeTheme.typography.brandTextInverted;
  }};
`;

export const Heading = styled(Text)`
  margin-top: 0;
  margin-bottom: ${spacing.space8};

  * {
    margin: 0;
  }
  p {
    font-weight: 500;
  }
  p > strong {
    font-weight: 900;
  }
`;

export const Description = styled(Text)`
  margin: 0;
`;

export const HeadingBlock = styled(PortableText)``;
